import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Wrench, Loader2, AlertTriangle } from "lucide-react";

/**
 * Cleanup date-exception rows whose `day_of_week` no longer matches their
 * `override_date`'s weekday (stale rows cloned from a different template day).
 * Re-stamp (non-destructive) or delete them, per channel or tenant-wide.
 */
export default function CleanupExceptionsDialog({ open, onClose, channelId, channelName, onDone }) {
  const [allChannels, setAllChannels] = useState(false);
  const [scan, setScan] = useState(null); // {scanned, matched}
  const [busy, setBusy] = useState(false);

  const runScan = async (all) => {
    setBusy(true);
    setScan(null);
    try {
      const r = await api.post("/schedule/exceptions/normalize-dow", {
        channel_id: all ? null : channelId,
        mode: "scan",
      });
      setScan(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (open) { setAllChannels(false); runScan(false); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleScope = (all) => { setAllChannels(all); runScan(all); };

  const apply = async (mode) => {
    setBusy(true);
    try {
      const r = await api.post("/schedule/exceptions/normalize-dow", {
        channel_id: allChannels ? null : channelId,
        mode,
      });
      if (mode === "restamp") toast.success(`Re-stamped ${r.data.restamped} stale row(s)`);
      else toast.success(`Deleted ${r.data.deleted} stale row(s)`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setBusy(false);
    }
  };

  const matched = scan?.matched ?? 0;

  const flushWeekly = async () => {
    if (!channelId) return;
    const typed = window.prompt(
      `This wipes the ENTIRE recurring weekly grid for "${channelName}" (date-exceptions are kept). ` +
      `Type the channel name exactly to confirm:`,
    );
    if (typed == null) return;
    setBusy(true);
    try {
      const r = await api.delete(`/schedule/weekly/${channelId}`, {
        params: { confirm_name: typed },
      });
      toast.success(`Flushed ${r.data.deleted} weekly row(s) from ${r.data.channel_name}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Flush failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="cleanup-exceptions-dialog">
        <DialogHeader className="pb-4">
          <DialogTitle className="font-display flex items-center gap-2">
            <Wrench className="w-4 h-4 text-cyan-400" /> Cleanup date-exceptions
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Finds override-date rows whose weekday no longer matches their date (usually left over from cloning a different template day). These no longer break playback, but keeping the grid tidy avoids confusion.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="inline-flex rounded-sm border border-border overflow-hidden" data-testid="cleanup-scope-switch">
            <button
              type="button"
              onClick={() => toggleScope(false)}
              disabled={busy || !channelId}
              data-testid="cleanup-scope-channel"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest ${!allChannels ? "bg-cyan-500/20 text-cyan-300" : "text-muted-foreground hover:text-foreground"}`}
            >
              {channelName || "This channel"}
            </button>
            <button
              type="button"
              onClick={() => toggleScope(true)}
              disabled={busy}
              data-testid="cleanup-scope-all"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-border ${allChannels ? "bg-cyan-500/20 text-cyan-300" : "text-muted-foreground hover:text-foreground"}`}
            >
              All channels
            </button>
          </div>

          <div className="rounded-sm border border-border bg-black/30 p-4 font-mono text-sm" data-testid="cleanup-scan-result">
            {busy && !scan ? (
              <span className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</span>
            ) : scan ? (
              <div className="flex items-center gap-2">
                {matched > 0
                  ? <><AlertTriangle className="w-4 h-4 text-amber-400" /><span><span className="text-amber-300 font-bold">{matched}</span> stale row(s)</span><span className="text-muted-foreground">of {scan.scanned} scanned</span></>
                  : <span className="text-emerald-300">No stale rows — schedule is tidy ✓</span>}
              </div>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>

          {/* Danger zone — reset a channel's recurring weekly grid entirely */}
          <div className="rounded-sm border border-red-500/40 bg-red-950/20 p-3 space-y-2" data-testid="cleanup-flush-weekly-section">
            <div className="text-[11px] font-mono uppercase tracking-widest text-red-300 flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5" /> Reset weekly grid
            </div>
            <p className="text-[11px] font-mono text-muted-foreground leading-relaxed">
              Wipes every recurring (non-exception) slot for <span className="text-red-300 font-bold">{channelName || "this channel"}</span> so you can rebuild or re-apply a template. Date-exceptions are left untouched. Use this to recover a channel whose weekly grid was overwritten by a bad template apply.
            </p>
            <Button
              variant="outline"
              onClick={flushWeekly}
              disabled={busy || !channelId || allChannels}
              data-testid="cleanup-flush-weekly-btn"
              className="rounded-sm border-red-500/50 text-red-300 hover:bg-red-950 h-8 text-xs"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null} Flush weekly grid…
            </Button>
            {allChannels && (
              <p className="text-[10px] font-mono text-amber-400/80">Switch scope back to a single channel to use this tool.</p>
            )}
          </div>
        </div>

        <DialogFooter className="pt-4 gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-sm border-border">Close</Button>
          <Button
            variant="outline"
            onClick={() => apply("delete")}
            disabled={busy || matched === 0}
            data-testid="cleanup-delete-btn"
            className="rounded-sm border-red-500/50 text-red-300 hover:bg-red-950"
          >
            Delete stale
          </Button>
          <Button
            onClick={() => apply("restamp")}
            disabled={busy || matched === 0}
            data-testid="cleanup-restamp-btn"
            className="tenant-btn rounded-sm font-display font-bold"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Re-stamp days
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
